import React from 'react';
import type { AdaptiveQuestionResult, TurnKind, TurnResult } from './types';
import { usePublicDiagnosticSession } from './session';
import { AdaptiveQuestionTurn } from './components/AdaptiveQuestionTurn';
import { AdaptiveFollowupTurn } from './components/AdaptiveFollowupTurn';
import { TimedQuestionTurn } from './components/TimedQuestionTurn';
import { SimulationBeatTurn } from './SimulationBeatTurn';
import { AskPamTurn } from './AskPamTurn';
import { PublicDiagnosticFlashcardTurn } from './PublicDiagnosticFlashcardTurn';

export interface TurnSlotProps {
  kind: TurnKind;
  turnId: 1 | 2 | 3 | 4 | 5 | 6;
  /** Prefetched payload when ready; null while loading. */
  payload: unknown | null;
  onComplete: (result: TurnResult) => void;
}

/** Default card when prefetch has no flashcard payload yet. */
const DEFAULT_FLASHCARD = {
  cardId: 'public-diag-card-1',
  front: 'What does a FIRST question usually ask you to do?',
  back: 'Identify the social worker’s immediate next step, not the long-term plan.',
};

function asFlashcardPayload(payload: unknown): {
  cardId: string;
  front: string;
  back: string;
} {
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>;
    if (
      typeof p.cardId === 'string' &&
      typeof p.front === 'string' &&
      typeof p.back === 'string'
    ) {
      return { cardId: p.cardId, front: p.front, back: p.back };
    }
  }
  return DEFAULT_FLASHCARD;
}

/**
 * Host for each turn kind. Wires Lane B/C/D bodies into the Lane A slot contract.
 */
export const TurnSlot: React.FC<TurnSlotProps> = ({ kind, turnId, payload, onComplete }) => {
  const state = usePublicDiagnosticSession();
  const turn1Result = state.results.find(
    (r): r is AdaptiveQuestionResult => r.turnId === 1 && 'questionId' in r
  );
  const usedQuestionIds = state.results
    .filter((r): r is AdaptiveQuestionResult => 'questionId' in r)
    .map((r) => r.questionId);

  switch (kind) {
    case 'adaptive_question':
      return <AdaptiveQuestionTurn onComplete={onComplete} />;
    case 'adaptive_followup':
      return (
        <AdaptiveFollowupTurn
          turn1Result={
            turn1Result ?? {
              questionId: 'unknown',
              correct: false,
              difficultyShown: 3,
            }
          }
          onComplete={onComplete}
        />
      );
    case 'simulation_beat':
      return (
        <SimulationBeatTurn onComplete={onComplete} />
      );
    case 'timed_question':
      return (
        <TimedQuestionTurn
          excludeQuestionIds={usedQuestionIds}
          onComplete={onComplete}
        />
      );
    case 'flashcard': {
      const card = asFlashcardPayload(payload);
      return (
        <PublicDiagnosticFlashcardTurn
          cardId={card.cardId}
          front={card.front}
          back={card.back}
          onComplete={onComplete}
        />
      );
    }
    case 'ask_pam':
      return <AskPamTurn sessionId={state.sessionId} onComplete={onComplete} />;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
};
